import { useCallback, useEffect } from 'react';
import { parseISO, format, isValid } from 'date-fns';

import { DatepickerComponent } from './Component';
import { ISO_FORMAT, getEndOfDay, getDate } from '../../utilities';
import type { DatepickerComponentProps } from './Component';

const parseValue = (value: string, valueFormat: string): Date | undefined => {
  const strict = getDate(value, valueFormat);
  if (strict) return strict;

  const iso = parseISO(value);
  return isValid(iso) ? iso : undefined;
};

export type DatepickerProps = Omit<
  DatepickerComponentProps,
  'value' | 'onChange'
> & {
  value?: string;
  onChange?: (newValue: string) => void;
  valueFormat?: string;

  disabled?: boolean;
  endOfDay?: boolean;
  cleanOnDisable?: boolean;
};

export const Datepicker: React.FC<DatepickerProps> = ({
  value,
  onChange,
  valueFormat = ISO_FORMAT,
  disabled,
  cleanOnDisable = false,
  endOfDay = false,
  ...rest
}) => {
  const thisValue = value ? parseValue(value, valueFormat) : undefined;
  const handleChange = useCallback(
    (newValue: Date | null) => {
      const formattedValue = newValue ? format(newValue, valueFormat) : '';
      const result = endOfDay ? getEndOfDay(formattedValue) : formattedValue;

      onChange?.(result);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueFormat, endOfDay]
  );

  useEffect(
    () => {
      if (!cleanOnDisable || !disabled) return;

      onChange?.('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cleanOnDisable, disabled]
  );

  return (
    <DatepickerComponent
      disabled={disabled}
      {...rest}
      value={thisValue}
      onChange={handleChange}
    />
  );
};
